import { useMemo } from 'react';
import { Info } from '@phosphor-icons/react';
import { VolunteerPhotoCompare } from './VolunteerPhotoCompare';

/**
 * MASI (Melasma Area and Severity Index) — URS-F216
 * Composite question type with 4 facial regions × 3 components each.
 *
 * Region weights:
 *   Frente (F) = 0.30
 *   Menton (C) = 0.10
 *   Malar Derecha (RM) = 0.30
 *   Malar Izquierda (LM) = 0.30
 *
 * Formula:
 *   MASI = 0.3·(AF·DF·HF) + 0.1·(AC·DC·HC) + 0.3·(ARM·DRM·HRM) + 0.3·(ALM·DLM·HLM)
 *   Range: 0 (no melasma) - 96 (maximum severity)
 *
 * Components:
 *   A (Area):        0-6 (None | <10% | 10-29% | 30-49% | 50-69% | 70-89% | 90-100%)
 *   D (Darkness):    0-4 (Normal | Apenas visible | Leve | Moderada | Severa)
 *   H (Homogeneity): 0-4 (Normal | Puntual | <1.5cm | >2cm | Uniforme)
 *
 * Value shape (stored as JSON string in the question response):
 *   { F:{A,D,H}, C:{A,D,H}, RM:{A,D,H}, LM:{A,D,H}, total: number }
 *
 * Props:
 *   value: string (JSON) or object
 *   onChange: (newValueAsJSONString) => void
 *   disabled?: boolean
 *   testIdPrefix?: string
 */

const REGIONS = [
  { key: 'F',  label: 'Frente',           weight: 0.30 },
  { key: 'C',  label: 'Mentón',           weight: 0.10 },
  { key: 'RM', label: 'Malar Derecha',    weight: 0.30 },
  { key: 'LM', label: 'Malar Izquierda',  weight: 0.30 },
];

const AREA_OPTS = [
  { v: 0, label: 'Ninguna' },
  { v: 1, label: '<10%' },
  { v: 2, label: '10-29%' },
  { v: 3, label: '30-49%' },
  { v: 4, label: '50-69%' },
  { v: 5, label: '70-89%' },
  { v: 6, label: '90-100%' },
];

const DARK_OPTS = [
  { v: 0, label: 'Normal' },
  { v: 1, label: 'Apenas visible' },
  { v: 2, label: 'Leve' },
  { v: 3, label: 'Moderada' },
  { v: 4, label: 'Severa' },
];

const HOMO_OPTS = [
  { v: 0, label: 'Normal' },
  { v: 1, label: 'Manchas puntuales' },
  { v: 2, label: 'Parches <1,5 cm' },
  { v: 3, label: 'Parches >2 cm' },
  { v: 4, label: 'Uniforme' },
];

function emptyValue() {
  return {
    F:  { A: 0, D: 0, H: 0 },
    C:  { A: 0, D: 0, H: 0 },
    RM: { A: 0, D: 0, H: 0 },
    LM: { A: 0, D: 0, H: 0 },
    total: 0,
    not_applicable: false,
  };
}

function parseValue(raw) {
  if (!raw) return emptyValue();
  if (typeof raw === 'object') return { ...emptyValue(), ...raw };
  try {
    const parsed = JSON.parse(raw);
    return { ...emptyValue(), ...(parsed || {}) };
  } catch {
    return emptyValue();
  }
}

function computeTotal(v) {
  const r = (k) => (v[k]?.A || 0) * (v[k]?.D || 0) * (v[k]?.H || 0);
  return Math.round((0.3 * r('F') + 0.1 * r('C') + 0.3 * r('RM') + 0.3 * r('LM')) * 10) / 10;
}

export function MasiRenderer({ value, onChange, disabled = false, testIdPrefix = 'masi', studyId, volunteerCode, currentVisitCode }) {
  const v = useMemo(() => parseValue(value), [value]);
  const total = useMemo(() => computeTotal(v), [v]);
  // URS-F511: cuando `not_applicable` esta marcado, deshabilitamos
  // los botones de las 4 regiones y el total se fuerza a null.
  const naDisabled = disabled || !!v.not_applicable;

  const update = (regionKey, component, newVal) => {
    if (naDisabled) return;
    const updated = {
      ...v,
      [regionKey]: { ...v[regionKey], [component]: newVal },
    };
    updated.total = computeTotal(updated);
    onChange(JSON.stringify(updated));
  };

  const toggleNotApplicable = (checked) => {
    if (disabled) return;
    // Al marcar/desmarcar, reseteamos los valores de regiones a 0 y
    // recalculamos el total (0 cuando NA).
    const next = checked
      ? { ...emptyValue(), not_applicable: true, total: 0 }
      : { ...v, not_applicable: false };
    next.total = checked ? 0 : computeTotal(next);
    onChange(JSON.stringify(next));
  };

  const ComponentRow = ({ regionKey, component, options, ariaLabel }) => (
    <div className="flex items-center gap-2 py-0.5">
      <span className="text-[10px] font-mono text-stone-500 w-16 shrink-0">{ariaLabel}</span>
      <div className="flex flex-wrap gap-1 flex-1">
        {options.map((o) => {
          const sel = (v[regionKey]?.[component] ?? 0) === o.v;
          return (
            <button
              key={o.v}
              type="button"
              disabled={naDisabled}
              data-testid={`${testIdPrefix}-${regionKey}-${component}-${o.v}`}
              onClick={() => update(regionKey, component, o.v)}
              title={o.label}
              className={`px-2 py-0.5 rounded text-[11px] font-mono border transition disabled:cursor-not-allowed disabled:opacity-50 ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-200 hover:border-[#1f3e80]/40'}`}
            >
              {o.v}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-renderer`}>
      {studyId && volunteerCode && (
        <div className="flex justify-end">
          <VolunteerPhotoCompare
            studyId={studyId}
            volunteerCode={volunteerCode}
            currentVisitCode={currentVisitCode}
            testIdPrefix={`${testIdPrefix}-photo`}
          />
        </div>
      )}
      {/* URS-F511: check "No aplica" a nivel escala MASI. */}
      <div className={`border rounded-sm px-3 py-2 flex items-center gap-2 ${v.not_applicable ? 'border-orange-300 bg-orange-50' : 'border-stone-200 bg-stone-50/60'}`}>
        <input
          type="checkbox"
          id={`${testIdPrefix}-na`}
          data-testid={`${testIdPrefix}-not-applicable`}
          checked={!!v.not_applicable}
          disabled={disabled}
          onChange={(ev) => toggleNotApplicable(ev.target.checked)}
          className="h-4 w-4 accent-orange-600 cursor-pointer disabled:cursor-not-allowed"
        />
        <label htmlFor={`${testIdPrefix}-na`} className={`text-sm select-none ${v.not_applicable ? 'text-orange-900' : 'text-stone-700'} ${disabled ? '' : 'cursor-pointer'}`}>
          No aplica — el voluntario no presenta melasma evaluable en esta visita
        </label>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 flex items-start gap-2">
        <Info size={14} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-[11px] text-amber-800 leading-tight">
          Valore cada región facial:
          <span className="block mt-0.5"><b>A</b> (Área): 0=ninguna, 1=&lt;10%, 2=10-29%, 3=30-49%, 4=50-69%, 5=70-89%, 6=90-100%.</span>
          <span className="block"><b>D</b> (Oscuridad): 0=normal, 1=apenas visible, 2=leve, 3=moderada, 4=severa.</span>
          <span className="block"><b>H</b> (Homogeneidad): 0=normal, 1=puntuales, 2=&lt;1,5 cm, 3=&gt;2 cm, 4=uniforme.</span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {REGIONS.map((r) => {
          const sub = (v[r.key]?.A || 0) * (v[r.key]?.D || 0) * (v[r.key]?.H || 0);
          const weighted = Math.round(sub * r.weight * 10) / 10;
          return (
            <div key={r.key} className="bg-white border border-stone-200 rounded-sm p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-semibold text-stone-800">{r.label}</h4>
                <span className="text-[10px] font-mono text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">peso {Math.round(r.weight * 100)}%</span>
              </div>
              <ComponentRow regionKey={r.key} component="A" options={AREA_OPTS} ariaLabel="A (Área)" />
              <ComponentRow regionKey={r.key} component="D" options={DARK_OPTS} ariaLabel="D (Oscur.)" />
              <ComponentRow regionKey={r.key} component="H" options={HOMO_OPTS} ariaLabel="H (Homog.)" />
              <div className="mt-1.5 pt-1.5 border-t border-stone-100 flex items-center justify-between text-[10px] text-stone-500">
                <span className="font-mono">A·D·H = {sub}</span>
                <span className="font-mono">× {r.weight} = <b className="text-stone-700">{weighted}</b></span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-r from-[#1f3e80] to-[#2a4f8a] text-white rounded-sm px-4 py-2.5 flex items-center justify-between" data-testid={`${testIdPrefix}-total`}>
        <span className="text-xs font-medium uppercase tracking-wider opacity-90">Puntuación total MASI</span>
        <span className="text-2xl font-mono font-bold">{total} <span className="text-sm opacity-70">/ 96</span></span>
      </div>
    </div>
  );
}
